// Typed client for the unified backend's catalog API. Same-origin in both dev
// (Vite proxies /api → Fastify) and prod, so no base URL is needed.
import type { CatalogFilters, CatalogPage, Facets, GameDetail, HighScores, ScoreAdapter } from "./types";

async function getJSON<T>(url: string): Promise<T> {
  const res = await fetch(url, { headers: { accept: "application/json" } });
  if (!res.ok) throw new Error(`${res.status} ${res.statusText} for ${url}`);
  return res.json() as Promise<T>;
}

function qs(params: Record<string, string | number | undefined | null>): string {
  const sp = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v !== undefined && v !== null && v !== "") sp.set(k, String(v));
  }
  const s = sp.toString();
  return s ? `?${s}` : "";
}

export function fetchCatalogPage(filters: CatalogFilters, cursor: string | null): Promise<CatalogPage> {
  return getJSON<CatalogPage>(
    `/api/catalog${qs({
      filter: filters.shelf,
      search: filters.search,
      category: filters.category,
      source: filters.source,
      sort: filters.sort,
      cursor: cursor ?? undefined,
    })}`,
  );
}

export function fetchFacets(shelf: string, source: string): Promise<Facets> {
  return getJSON<Facets>(`/api/catalog/facets${qs({ filter: shelf, source })}`);
}

export function fetchGame(id: string, catalog?: string): Promise<GameDetail> {
  return getJSON<GameDetail>(`/api/catalog/${encodeURIComponent(id)}${qs({ catalog })}`);
}

// Leaderboard read (proxied to the yog-server scoring API). Score *submission*
// stays in the un-bundled highscores.js capture path.
export function fetchHighScores(gameId: string, period: string, limit = 25): Promise<HighScores> {
  return getJSON<HighScores>(`/api/highscores${qs({ game: gameId, period, limit })}`);
}

// The scoring adapter registry decides whether a game has a usable leaderboard.
export async function fetchScoreAdapter(gameId: string): Promise<ScoreAdapter | null> {
  try {
    const reg = await getJSON<{ games?: Record<string, ScoreAdapter> }>("/data/score-adapters.json");
    return reg.games?.[gameId] ?? null;
  } catch {
    return null;
  }
}
